import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Logo from '@components/logo';
// import { ICON_TYPES } from '@components/moduleIcon';
import {
  AnalysisPermissionKeys,
  InsightsPermissionKeys,
  projectRole,
  USER_ROLE_MODEL,
  getMenuList,
  getInsightsDropdownList,
} from '@model';
import { mergeTwoRoutes, convertDefaultToNavProps } from './helper';
import NavButtonGroup from './navButtonGroup';
import action from '@flux/action';
import { getSessionStorage } from '@utils/windowUtil';

function AppSiderBar({
  routes,
  rootPath: _rootPath,
  currentId,
  // permission,
  customizedModules,
  isClient,
  isPwcReadOnly,
  changeRoutes,
  matchBreadCrumb,
  originAccessibleModules,
}) {
  const rootPath = _rootPath.trim() === '/' ? '/' : `${_rootPath.trim()}/`;
  const location = useLocation();
  const projectId = getSessionStorage('currentId');

  const [routesToShow, setRoutesToShow] = useState([]);

  const formatRedirectLink = (path) => {
    if (path === 'project-detail/:id') {
      return `/project-detail/${projectId}`;
    }
    if (path === 'integrationhub') {
      return `/integrationhub?${encodeURIComponent(`currentId=${projectId}`)}`;
    }
    return `${rootPath}${path}`;
  };

  const getNavItems = useCallback(
    (_routes) =>
      _routes
        .map((route, index) => {
          const displayName = route.displayName;
          const label = route.displayName;
          const { iconId, to, isExternal, iframe, type, routes } = route;
          const hide = Boolean(route.showWhenHasId && !currentId) || !iconId;

          if (hide) {
            return null;
          }
          const toPath = isExternal && !iframe ? to : formatRedirectLink(to);
          return {
            index,
            displayName,
            iconId,
            to: toPath,
            isExternal,
            iframe,
            type,
            label,
            value: route.value,
            subRoutes: convertSubRoutes(route.value, routes, toPath),
          };
        })
        .filter((e) => e !== null),
    [location, rootPath, currentId, customizedModules, originAccessibleModules],
  );

  const { singleCompanyInstance, projectType } = useSelector((state) => ({
    singleCompanyInstance: state.projects.saveData.singleCompanyInstance,
    projectType: state.common.projectCompanyInfo.instanceType,
  }));

  const convertSubRoutes = (value, routes, to) => {
    const subRoutes = [];

    if (routes) {
      let index = 1;

      if (value === 'insight') {
        const insightMenuList = getInsightsDropdownList({
          permissionCodes: originAccessibleModules?.moduleList,
          isDivestiture: singleCompanyInstance === 'Divestiture',
          projectType,
          originPermissions: customizedModules?.find?.(
            (i) => i.value === 'insight',
          )?.originPermissions,
        });
        insightMenuList.forEach((e) => {
          subRoutes.push({
            menuId: e.id,
            index: index++,
            displayName: e.title,
            to: `${to}/${e.id}?projectId=${projectId}`,
            label: e.title,
          });
        });
      }

      routes.forEach((route) => {
        const { path } = route;

        if (path !== '/') {
          if (value === 'analysis') {
            const analysisMenuList = getMenuList({
              permissionCodes: originAccessibleModules?.moduleList,
              originPermissions: customizedModules?.find?.(
                (i) => i.value === 'analysis',
              )?.originPermissions,
            });
            analysisMenuList.forEach((e) => {
              subRoutes.push({
                menuId: e.id,
                index: index++,
                displayName: e.title,
                to: `${to}/${e.id}?projectId=${projectId}`,
                label: e.title,
              });
            });
          }
        }
      });
    }
    return subRoutes;
  };

  useEffect(() => {
    if (
      typeof customizedModules === 'undefined' ||
      // typeof isClient === 'undefined' ||
      !currentId ||
      !originAccessibleModules
    ) {
      const tempRoute = routes.map(convertDefaultToNavProps).slice(0, 2);
      setRoutesToShow(getNavItems(tempRoute));
      changeRoutes(tempRoute);
      matchBreadCrumb();
      return;
    }
    const showAnalysisModule = originAccessibleModules?.moduleList?.some(
      (key) => AnalysisPermissionKeys.includes(key),
    );

    const showInsightsModule = originAccessibleModules?.moduleList?.some(
      (key) => InsightsPermissionKeys.includes(key),
    );

    const shouldDisplay = (module) =>
      originAccessibleModules?.moduleList?.includes(module.value) ||
      (module.value === 'insight' && showInsightsModule) ||
      (module.value === 'analysis' && showAnalysisModule) ||
      // the permission value of questionnaire module is 'complexity'
      (originAccessibleModules?.moduleList?.includes('complexity') &&
        module.value === 'questionnaire');

    const moduleList = customizedModules.filter(shouldDisplay).map((i) => ({
      ...i,
      iframe: i.type === 'textContainer' ? true : i.iframe,
    }));

    const mergedRoutes = mergeTwoRoutes(routes, moduleList);
    const res = getNavItems(mergedRoutes);

    setRoutesToShow(res);
    changeRoutes(mergedRoutes);
  }, [currentId, location, customizedModules, originAccessibleModules]);

  return (
    <Fragment>
      <Logo />
      <div className="app-sidebar-body">
        <NavButtonGroup
          routes={routesToShow}
          currentId={currentId}
          isClient={isClient}
          isPwcReadOnly={isPwcReadOnly}
        />
      </div>
    </Fragment>
  );
}

AppSiderBar.defaultProps = {
  routes: [],
  rootPath: '/',
};

AppSiderBar.propTypes = {
  routes: PropTypes.array,
  rootPath: PropTypes.string,
};

const mapStateToProps = (state) => ({
  // permission: state.projects.userPermission,
  customizedModules: state.projects.customizedModules,
  originAccessibleModules: state?.projects?.originAccessibleModules,
  isClient: state.login.userInfo.role === USER_ROLE_MODEL.CLIENT,
  isPwcReadOnly:
    state.common.projectCompanyInfo?.currentUserPermission ===
    projectRole['View-Only'],
});

const mapDispatchToData = (dispatch) => ({
  changeRoutes(routes) {
    dispatch(action.changeRoutes(routes));
  },
  matchBreadCrumb() {
    dispatch(action.matchBreadCrumb());
  },
});

export default connect(mapStateToProps, mapDispatchToData)(AppSiderBar);
